/* Én sak i Skarpenord Configurator som levende replika: sakhodet med fanene Oversikt · Omfang · Ventiler · Konfigurasjon · Kalkyle · Tilbud,
   og «Bygg 118» som går fra ventilliste til teknisk godkjent tilbud. Statiske data, samme formspråk som appen. */
import { useEffect, useState } from "react";
import { ArrowRight, Check, ChevronDown, Download, FileText, Filter, Languages, MoreHorizontal, Search, Share2, ShieldCheck, Sparkles, Workflow } from "lucide-react";
import { Segmented } from "@/components/layout/Segmented";
import { QuoteView } from "@/components/canvas/QuoteView";
import { Avatar, Ghost, Pill, Primary, Shell, WorkPanel } from "./SkShell";
import { VisueltBody } from "./SkVisuelt";
import { CASE, COST_BASIS, ENGINEER, ETTERKALKYLE, GROUPS, QUOTE_TEXT_META, QUOTE_VM, STATUS_LABEL, TYPES, USER, VALVES, VALVE_STATS, nok } from "./skMockData";

/* manuset: ett slag per steg */
export const SCRIPT = [
  { tab: "oversikt", stage: "konfig", ms: 4200 },
  { tab: "ventiler", stage: "konfig", ms: 5200, toast: ["54 ventiler lest fra COMOS-eksporten", "Hver verdi sporet til ark og celle · 46 forslag · 6 krever ingeniør · 2 må avklares"] },
  { tab: "konfig", stage: "konfig", ms: 4800, toast: ["9 produkttyper · én leveranse", "Aktuatorer, SVC, HPU, styring, dokumentasjon og idriftsettelse lagt i kalkylen"] },
  { tab: "kalkyle", stage: "kalkyle", ms: 4800, toast: ["Kalkylen er komplett – DG 28,0 %", "54 aktuatorer · kostgrunnlag synlig per produkttype · ingen poster uten pris"] },
  { tab: "tilbud", stage: "tilbud", ms: 4600, toast: ["Versjon 1 frosset fra kalkylen", "2 822 500 kr eks. mva · følgebrev og forbehold fra tekstbiblioteket"] },
  { tab: "tilbud", stage: "kontroll", ms: 5400, toast: [`Teknisk godkjent av ${ENGINEER.name}`, "6 enkeltvirkende vurdert · bundet til konfigurasjonen · klar til utsending"] },
];

const STATUS = { konfig: "Konfigureres", kalkyle: "Kalkuleres", tilbud: "Tilbud klart til godkjenning", kontroll: "Teknisk godkjent · klar til utsending" };
const TABS = [
  { key: "oversikt", label: "Oversikt" },
  { key: "omfang", label: "Omfang" },
  { key: "ventiler", label: "Ventiler" },
  { key: "konfig", label: "Konfigurasjon" },
  { key: "kalkyle", label: "Kalkyle" },
  { key: "tilbud", label: "Tilbud" },
  { key: "meldinger", label: <>Meldinger <span className="rounded-full bg-black/[0.07] px-1.5 text-[11px] tabular-nums text-foreground/70">2</span></>, divider: true },
];

/* ------------------------------------------------------------------ hodet */

const Head = ({ tab, onTab, stage, nar, layoutId }) => (
  <header className={`grid shrink-0 items-center gap-3 border-b border-black/[0.05] bg-white/90 ${nar ? "h-auto grid-cols-1 px-3 py-2" : "h-[60px] grid-cols-[1fr_auto_1fr] px-4"}`}>
    <div className="min-w-0 leading-tight">
      <div className="truncate text-[15.5px] font-semibold text-foreground">{CASE.name}</div>
      <div className="truncate text-[12.5px] text-muted-foreground">{CASE.customer} · {STATUS[stage]}</div>
    </div>
    <Segmented value={tab} onChange={onTab} items={TABS.map((t) => ({ ...t, testId: `sk-sak-tab-${t.key}` }))} size={nar ? "sm" : "md"} layoutId={layoutId} ariaLabel="Faner" scroll={nar} className={nar ? "max-w-full" : ""} />
    {!nar && (
      <div className="flex items-center justify-end gap-1.5">
        <Ghost icon={Sparkles}>Assistent <span className="rounded-full bg-primary px-1.5 font-mono text-[10px] font-semibold text-white">1</span></Ghost>
        <Ghost icon={Share2}>Del</Ghost>
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground/70"><MoreHorizontal className="h-4 w-4" /></span>
      </div>
    )}
  </header>
);

/* ------------------------------------------------------------------ Oversikt */

const Facts = () => (
  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-y border-black/[0.08] py-4 text-[13px] sm:grid-cols-3">
    {[["Kunde", CASE.customer], ["Fartøy", CASE.vessel], ["Levering", CASE.period_text], ["Systemtrykk", `${CASE.pressure} bar · bekreftet`], ["Tilbudsfrist", CASE.deadline], ["Kontakt", CASE.contact]].map(([l, v]) => (
      <div key={l} className="min-w-0"><dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{l}</dt><dd className="mt-0.5 truncate font-medium text-foreground/90">{v}</dd></div>
    ))}
  </dl>
);

const OversiktBody = ({ stage, nar }) => {
  const done = stage === "kontroll";
  const next = done ? `Send tilbudet til ${CASE.customer}` : stage === "tilbud" ? `Send versjon 1 til teknisk godkjenning` : stage === "kalkyle" ? "Fullfør kalkylen · 2 priser venter bekreftelse" : "6 ventiler krever ingeniørvurdering · CA-V-106 først";
  return (
    <div className={`mx-auto w-full max-w-[860px] ${nar ? "px-4 py-5" : "px-8 py-7"}`}>
      <div className="flex items-center justify-between gap-4 rounded-[14px] border border-black/[0.06] bg-[hsl(var(--sidebar))] px-4 py-3.5">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Neste steg</div>
          <div className="mt-0.5 truncate text-[14.5px] font-semibold text-foreground">{next}</div>
        </div>
        <Primary icon={ArrowRight}>{done ? "Send" : stage === "tilbud" ? "Til godkjenning" : stage === "kalkyle" ? "Kalkylen" : "Ventiler"}</Primary>
      </div>
      <div className={`mt-6 grid gap-5 ${nar ? "grid-cols-1" : "grid-cols-3"}`}>
        {[
          ["Ventiler", `${VALVE_STATS.total} ventiler`, `${VALVE_STATS.forslag} forslag · ${VALVE_STATS.ingenior} krever ingeniør · ${VALVE_STATS.avklar} avklares`],
          ["Kalkyle", stage === "konfig" ? "Basis lagt inn" : "Komplett", stage === "konfig" ? "9 produkttyper · 2 priser venter" : `${CASE.sumText} kr · DG ${CASE.dgPct}`],
          ["Tilbud", done ? "Versjon 1 godkjent" : stage === "tilbud" ? "Versjon 1 · utkast" : "Ingen versjon ennå", done ? `teknisk av ${ENGINEER.name}` : `frist ${CASE.deadline}`],
        ].map(([k, v, s]) => (
          <div key={k}>
            <div className="k-label">{k}</div>
            <div className="mt-1 text-[17px] font-semibold tracking-[-0.01em] text-foreground">{v}</div>
            <div className="mt-0.5 text-[12.5px] text-muted-foreground">{s}</div>
          </div>
        ))}
      </div>
      <div className="mt-7"><Facts /></div>
      <div className="mt-7">
        <div className="k-label">Slik ble arbeidet opprettet</div>
        <p className="mt-1.5 text-[14px] leading-[1.6] text-foreground/85">Fra e-post fra {CASE.contact} i dag 08:12. Assistenten leste 3 dokumenter og la inn 54 ventiler fra COMOS-eksporten (ark Query, rad 3–56) – hver verdi med sporing til cellen, originalfilen urørt.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {["Ventilliste_B118_rev_B.xls · 54 rader", "PID_lastesystem_B118.pdf", "Spesifikasjon_VRC_B118.pdf"].map((f) => (
            <span key={f} className="inline-flex h-8 items-center gap-1.5 rounded-full border border-black/[0.08] bg-white px-3 text-[12.5px] text-foreground/80"><FileText className="h-3.5 w-3.5 text-muted-foreground" /> {f}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ Ventiler (grid + inspektør) */

const SEL = "CA-V-101";
const COLS = ["Tag", "Type", "DN", "BTO Nm", "Flens", "Fail", "Indikering", "Forslag", "SF", "Status"];

const Cell = ({ children, mono, right, muted, className = "" }) => (
  <td className={`h-9 whitespace-nowrap border-r border-black/[0.04] px-2.5 text-[12.5px] ${mono ? "font-mono text-[12px]" : ""} ${right ? "text-right tabular-nums" : ""} ${muted ? "text-muted-foreground" : "text-foreground"} ${className}`}>{children}</td>
);

const HIDE = { full: ["Fail", "Indikering"], compact: ["Fail"] };

/* the phone: one card per valve – tag and status first, then the proposal and the facts behind it */
const NarrowList = () => (
  <ul className="divide-y divide-black/[0.05]" data-testid="sk-mock-grid-narrow">
    {VALVES.slice(0, 10).map((v) => (
      <li key={v.tag} className={`px-3 py-2.5 ${v.tag === SEL ? "bg-[hsl(var(--primary)/0.07)]" : ""}`} data-status={v.status}>
        <div className="flex items-center justify-between gap-2"><span className="font-mono text-[13px] font-semibold text-foreground">{v.tag}</span><Pill status={v.status}>{STATUS_LABEL[v.status]}</Pill></div>
        <div className={`mt-1 font-mono text-[12.5px] ${v.code === "–" ? "text-muted-foreground" : "text-foreground"}`}>{v.code}{v.sf !== "–" ? <span className="ml-2 text-muted-foreground">SF {v.sf}</span> : null}</div>
        <div className="mt-0.5 text-[12px] text-muted-foreground">{v.type} DN {v.dn} · {v.bto == null ? "moment mangler" : `BTO ${v.bto} Nm`} · {v.flange} · {v.fail} · {v.ind}</div>
      </li>
    ))}
  </ul>
);

const Grid = ({ nar, compact }) => {
  if (nar) return <NarrowList />;
  const hide = HIDE[compact ? "compact" : "full"];
  const show = (c) => !hide.includes(c);
  return (
    <table className="w-full border-collapse" data-testid="sk-mock-grid">
      <thead>
        <tr className="sticky top-0 z-10 bg-[hsl(var(--sidebar))] text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
          {COLS.filter(show).map((c) => <th key={c} className={`h-8 border-b border-r border-black/[0.06] px-2.5 font-semibold ${["DN", "BTO Nm", "SF"].includes(c) ? "text-right" : ""}`}>{c}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y divide-black/[0.05]">
        {VALVES.slice(0, 16).map((v) => {
          const on = v.tag === SEL;
          return (
            <tr key={v.tag} className={on ? "bg-[hsl(var(--primary)/0.07)]" : v.status === "avklar" ? "bg-[hsl(var(--status-bad)/0.03)]" : ""} data-status={v.status}>
              <Cell mono className={`sticky left-0 z-[1] font-medium ${on ? "bg-[hsl(var(--primary)/0.07)]" : "bg-white"}`}>{v.tag}</Cell>
              <Cell>{v.type}</Cell>
              <Cell right>{v.dn}</Cell>
              <Cell right muted={v.bto == null}>{v.bto == null ? "–" : v.bto}</Cell>
              {show("Flens") && <Cell mono>{v.flange}</Cell>}
              {show("Fail") && <Cell>{v.fail}</Cell>}
              {show("Indikering") && <Cell>{v.ind}</Cell>}
              <Cell mono muted={v.code === "–"}>{v.code}</Cell>
              {show("SF") && <Cell right muted={v.sf === "–"}>{v.sf}</Cell>}
              <Cell><Pill status={v.status}>{STATUS_LABEL[v.status]}</Pill>{v.note && compact ? <span className="ml-2 text-[11.5px] text-muted-foreground">{v.note}</span> : null}</Cell>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const Inspector = () => {
  const v = VALVES.find((x) => x.tag === SEL);
  return (
    <aside className="mb-noscroll flex w-[400px] shrink-0 flex-col overflow-hidden border-l border-black/[0.06] bg-white px-6 py-5" data-testid="sk-mock-inspector">
      <div className="flex items-center justify-between"><span className="k-ref">{v.tag} · {v.type.toLowerCase()}ventil DN {v.dn} · {v.sys}</span><Pill status={v.status}>{STATUS_LABEL[v.status]}</Pill></div>
      <h2 className="mt-1 font-mono text-[18px] font-semibold leading-tight tracking-[-0.01em] text-foreground">{v.code}</h2>
      <p className="mt-1 text-[12.5px] leading-[1.5] text-muted-foreground">RA2 · dobbeltvirkende med mekanisk lås · størrelse 10 (500 Nm ved 180 bar, F10) · hydraulikk, standard sylinder · materiale B, stålsylinder · BSP · normal drift · indikeringsboks.</p>
      <div className="mt-5 k-label">Dimensjonering mot {CASE.pressure} bar</div>
      <ul className="mt-2 space-y-1.5 text-[13px]">
        {[["Moment ved 130 bar · RA2 10", "551 Nm", "ok"], ["BTO 420 Nm × SF 1,3 (kule)", "546 Nm · oppfylt", "ok"], ["ETC 390 Nm × SF 1,3", "507 Nm · oppfylt", "ok"], ["MAST 1 100 Nm – øvre grense", "551 Nm · under", "ok"], ["Flens ISO 5211 F10 ↔ F10", "ingen adapter", "ok"]].map(([k, val, s]) => (
          <li key={k} className="flex items-start justify-between gap-3"><span className="inline-flex items-center gap-2 text-foreground/85"><Check className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-ok))]" strokeWidth={2.4} />{k}</span><span className="shrink-0 tabular-nums text-muted-foreground">{val}</span></li>
        ))}
      </ul>
      <div className="mt-5 k-label">Stykkliste</div>
      <ul className="mt-2 divide-y divide-black/[0.05] text-[12.5px]">
        {[["RA2 10 H1 0B B N", "Aktuator"], ["300.631.42", "Indikeringsboks OP/CL · Namur"], ["306.023.68", "Nødbetjeningsblokk messing"], ["RES.270.21", "Hylse rund, boret"], ["313.979.03 · 009.080.06", "Kile · låsering"]].map(([p, n]) => (
          <li key={p} className="flex items-center gap-3 py-1.5"><span className="k-ref w-[150px] shrink-0 truncate">{p}</span><span className="truncate text-foreground/85">{n}</span></li>
        ))}
      </ul>
      <div className="mt-5 k-label">Kilde</div>
      <div className="mt-1.5 text-[12.5px] text-foreground/80">Ventilliste_B118_rev_B.xls · Query · rad 3 · BTO i celle <span className="k-ref">J3</span>, flens <span className="k-ref">N3</span> → <span className="text-[hsl(var(--navy))]">Åpne cellen</span></div>
      <div className="mt-auto flex items-center justify-between gap-2 pt-5">
        <span className="inline-flex items-center gap-2 text-[12.5px] text-muted-foreground"><Avatar size={22} initials={ENGINEER.initials} color={ENGINEER.color} /> Godkjent teknisk av {ENGINEER.name} · i dag</span>
        <span className="inline-flex h-8 items-center gap-1.5 rounded-full border border-black/[0.09] bg-white px-3 text-[12.5px] font-medium"><Workflow className="h-3.5 w-3.5" /> Visuelt</span>
      </div>
    </aside>
  );
};

export const VentilerBody = ({ nar, mode, ns = "sk-sak" }) => {
  const compact = mode === "compact";
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="sk-mock-ventiler">
      <div className={`flex shrink-0 flex-wrap items-center gap-2 border-b border-black/[0.05] ${nar ? "px-3 py-2" : "px-4 py-2"}`}>
        {!nar && <Segmented value="arbeid" onChange={() => {}} items={[{ key: "arbeid", label: "Arbeidsoversikt" }, { key: "dim", label: "Dimensjonering" }, { key: "sty", label: "Styring og tilkobling" }, { key: "alle", label: "Alle felt" }]} size="sm" layoutId={`${ns}-ventiler-view`} ariaLabel="Visning" />}
        <span className="inline-flex h-8 items-center gap-2 rounded-full border border-black/[0.08] bg-white px-3 text-[12.5px] text-muted-foreground"><Search className="h-3.5 w-3.5" /> Søk tag, system …</span>
        <span className="inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-[12.5px] font-medium text-foreground/80"><Filter className="h-3.5 w-3.5" /> Filter</span>
        <Pill status="ingenior">{VALVE_STATS.ingenior} krever ingeniør</Pill>
        <Pill status="avklar">{VALVE_STATS.avklar} må avklares</Pill>
        <span className="ml-auto text-[12.5px] text-muted-foreground tabular-nums">{VALVE_STATS.total} ventiler · {CASE.pressure} bar</span>
        {!nar && <Primary icon={Download}>Eksporter</Primary>}
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="mb-noscroll min-w-0 flex-1 overflow-hidden"><Grid nar={nar} compact={compact} /></div>
        {!nar && !compact && <Inspector />}
      </div>
      {!nar && <div className="flex h-8 shrink-0 items-center gap-3 border-t border-black/[0.05] px-4 text-[11.5px] text-muted-foreground"><span className="font-medium text-foreground">{SEL}</span><span>·</span><span>Rad 1 av 54</span><span>·</span><span>⌘C kopier · ⌘V lim inn · ⌘D fyll ned · ⌘Z angre</span><span className="ml-auto inline-flex items-center gap-1 text-[hsl(var(--status-ok))]"><Check className="h-3 w-3" strokeWidth={2.6} /> Lagret</span></div>}
    </div>
  );
};

/* ------------------------------------------------------------------ Konfigurasjon */

const Verdict = ({ k, n, label, status }) => (
  <div className="rounded-[12px] border border-black/[0.06] bg-white px-3.5 py-3">
    <div className="k-figure tabular-nums text-foreground" data-num="">{n}</div>
    <div className="mt-1 flex items-center gap-1.5 text-[12.5px] font-medium text-foreground/85">{status ? <Pill status={status}>{label}</Pill> : label}</div>
  </div>
);

const KonfigBody = ({ nar }) => (
  <div className={`mx-auto w-full max-w-[1180px] ${nar ? "px-4 py-4" : "px-8 py-6"}`} data-testid="sk-mock-konfig">
    <div className="flex items-start justify-between gap-4">
      <div><h2 className="text-[20px] font-semibold tracking-[-0.01em] text-foreground">54 ventiler · 9 produkttyper · én leveranse</h2><p className="mt-1 text-[13.5px] text-muted-foreground">Dimensjonert mot {CASE.pressure} bar. Enkeltvirkende aktuatorer går alltid til ingeniør – kurvegrunnlaget er ikke dokumentert.</p></div>
      {!nar && <Primary icon={ArrowRight}>6 til ingeniør</Primary>}
    </div>
    <div className={`mt-5 grid gap-3 ${nar ? "grid-cols-2" : "grid-cols-5"}`}>
      <Verdict n={VALVE_STATS.forslag} label="Forslag" status="forslag" />
      <Verdict n={VALVE_STATS.godkjent} label="Godkjent" status="godkjent" />
      <Verdict n={VALVE_STATS.ingenior} label="Krever ingeniør" status="ingenior" />
      <Verdict n={VALVE_STATS.avklar} label="Må avklares" status="avklar" />
      <Verdict n={VALVE_STATS.types} label="Produkttyper" />
    </div>
    <div className="mt-7 k-label">Produkttyper</div>
    <table className="mt-2 w-full border-collapse text-[13px]">
      <thead><tr className="text-[11.5px] font-medium text-muted-foreground"><th className="pb-2 text-left font-medium">Typekode</th><th className="pb-2 text-right font-medium">Antall</th>{!nar && <th className="pb-2 text-left font-medium pl-6">Familie</th>}{!nar && <th className="pb-2 text-left font-medium">Flens</th>}<th className="pb-2 text-right font-medium">SF</th><th className="pb-2 text-right font-medium">Status</th></tr></thead>
      <tbody className="divide-y divide-black/[0.06]">
        {TYPES.slice(0, nar ? 5 : 8).map((t) => (
          <tr key={t.code} className="k-row">
            <td className="py-2 font-mono text-[12.5px] font-medium text-foreground">{t.code}</td>
            <td className="py-2 text-right tabular-nums text-foreground">{t.n}</td>
            {!nar && <td className="py-2 pl-6 text-foreground/80">{t.family}</td>}
            {!nar && <td className="py-2 font-mono text-[12px] text-foreground/80">{t.adapter}</td>}
            <td className="py-2 text-right tabular-nums text-foreground/80">{t.sf}</td>
            <td className="py-2 text-right"><Pill status={t.status}>{STATUS_LABEL[t.status]}</Pill></td>
          </tr>
        ))}
      </tbody>
    </table>
    {!nar && (
      <>
        <div className="mt-7 flex items-center justify-between"><span className="k-label">Leveransen · Basis</span><span className="text-[12.5px] text-muted-foreground">synkronisert med kalkylen · i dag 09:41</span></div>
        <ul className="mt-2 grid grid-cols-3 gap-x-8 divide-y divide-black/[0.05] text-[13px] [&>li]:border-0">
          {GROUPS.map((g) => <li key={g.key} className="flex items-center justify-between py-2"><span className="text-foreground/85">{g.name}</span><span className="tabular-nums text-muted-foreground">{g.n} {g.n === 1 ? "post" : "poster"}</span></li>)}
        </ul>
      </>
    )}
  </div>
);

/* ------------------------------------------------------------------ Omfang */

const OmfangBody = ({ nar }) => (
  <div className={`mx-auto w-full max-w-[980px] ${nar ? "px-4 py-4" : "px-8 py-6"}`} data-testid="sk-mock-omfang">
    <div className="flex items-center justify-between gap-3"><h2 className="text-[20px] font-semibold tracking-[-0.01em] text-foreground">Leveransen</h2>{!nar && <Primary icon={ArrowRight}>Gå til kalkylen</Primary>}</div>
    <ul className="mt-4 divide-y divide-black/[0.06]">
      {GROUPS.map((g) => (
        <li key={g.key} className="k-row flex items-center gap-3 py-2.5"><span className="k-title min-w-0 flex-1 truncate text-[14px] font-medium text-foreground">{g.name}</span><span className="text-[12.5px] text-muted-foreground tabular-nums">{g.n} {g.n === 1 ? "post" : "poster"}</span><span className="inline-flex h-7 items-center rounded-full bg-black/[0.05] px-2.5 text-[11.5px] font-medium text-foreground/80">Inkludert</span></li>
      ))}
      {[["Reservedelspakke for to års drift", "Opsjon"], ["Nødbetjeningsblokk i AISI 316 – 54 stk", "Opsjon"], ["Rør mellom HPU, skap og aktuatorer", "Ikke tilbudt"]].map(([n, s]) => (
        <li key={n} className="k-row flex items-center gap-3 py-2.5"><span className="k-title min-w-0 flex-1 truncate text-[14px] text-foreground/80">{n}</span><span className="inline-flex h-7 items-center rounded-full border border-black/[0.08] px-2.5 text-[11.5px] font-medium text-muted-foreground">{s}</span></li>
      ))}
    </ul>
    <div className="mt-7 k-label">Forutsetninger og avklaringer</div>
    <ul className="mt-2 space-y-2 text-[13.5px]">
      {["Ventilenes toppflens bekreftes mot ventilleverandørens tegninger før produksjon.", "6 ventiler krever ingeniørvurdering (enkeltvirkende) – fra konfigurasjonen", "2 ventiler mangler moment – avklaring sendt til Fjordane Verft 14. okt."].map((t, i) => <li key={t} className="flex gap-2.5"><span className={`mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full ${i ? "bg-[hsl(var(--status-warn))]" : "bg-black/[0.3]"}`} />{t}</li>)}
    </ul>
  </div>
);

/* ------------------------------------------------------------------ Kalkyle */

const StageDots = ({ stage }) => {
  const idx = stage === "kontroll" ? 2 : 1;
  return (
    <ol className="flex items-center gap-4 text-[12.5px]">
      {["Estimert", "Komplett", "Godkjent"].map((l, i) => (
        <li key={l} className={`flex items-center gap-1.5 ${i <= idx ? "text-foreground" : "text-muted-foreground/70"}`}>
          {i < idx ? <Check className="h-3.5 w-3.5 text-[hsl(var(--status-ok))]" strokeWidth={2.4} /> : <span className={`h-2 w-2 rounded-full ${i === idx ? "bg-primary" : "border border-black/[0.25]"}`} />}
          <span className={i === idx ? "font-semibold" : "font-medium"}>{l}</span>
        </li>
      ))}
    </ol>
  );
};

const Line = ({ title, sub, right, tone }) => (
  <div className="flex items-start justify-between gap-3 border-b border-black/[0.06] py-3 last:border-0">
    <div className="min-w-0"><div className={`text-[14px] font-medium ${tone === "muted" ? "text-muted-foreground" : "text-foreground"}`}>{title}</div>{sub ? <div className="mt-0.5 text-[12.5px] text-muted-foreground">{sub}</div> : null}</div>
    {right ? <span className="shrink-0 text-[12.5px] text-muted-foreground">{right}</span> : <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />}
  </div>
);

export const KalkyleBody = ({ stage, nar, ns = "sk-sak" }) => {
  const ctrl = stage === "kontroll";
  return (
    <div className={`mx-auto w-full max-w-[1180px] ${nar ? "px-4 py-4" : "px-8 py-5"}`} data-testid="sk-mock-kalkyle">
      <div className="flex items-center justify-between gap-3">
        <Segmented value="summary" onChange={() => {}} items={[{ key: "summary", label: "Økonomi" }, { key: "items", label: "Leveranseposter" }]} size="sm" layoutId={`${ns}-kalkyle-view`} ariaLabel="Visning" />
        <div className="flex items-center gap-1.5">
          <Primary>{ctrl ? "Åpne tilbudet" : "Gå til tilbudet"}</Primary>
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground/70"><MoreHorizontal className="h-4 w-4" /></span>
        </div>
      </div>
      <div className={`mt-6 grid gap-x-12 gap-y-8 ${nar ? "grid-cols-1" : "grid-cols-[1fr_minmax(280px,34%)]"}`}>
        <div>
          <StageDots stage={stage} />
          <div className="mt-5 k-label">Fastprissum</div>
          <div className="k-hero mt-1 tabular-nums text-foreground" data-num="">{CASE.sumText}</div>
          <div className="k-meta mt-1">alle 7 poster er ferdig · 1 etter regning · 2 opsjoner utenfor</div>
          <dl className={`mt-6 grid gap-x-8 gap-y-4 ${nar ? "grid-cols-2" : "grid-cols-4"}`}>
            {[["Kost", CASE.cost, "Dynamics · 2 typer seriejustert"], ["Dekningsgrad", CASE.dg, CASE.dgPct], ["Aktuatorer", "54", "9 produkttyper · Rjukan"], ["Valuta", "NOK", "kunde faktureres i NOK"]].map(([k, v, s]) => (
              <div key={k}><dt className="k-label">{k}</dt><dd className="k-figure mt-0.5 tabular-nums text-foreground" data-num="">{v}</dd><dd className="k-meta mt-0.5">{s}</dd></div>
            ))}
          </dl>
          <div className="mt-7 flex items-center justify-between"><span className="k-label">Kostgrunnlag per produkttype</span><span className="text-[12.5px] text-muted-foreground">hentet fra Dynamics 14. okt.</span></div>
          <table className="mt-2 w-full border-collapse text-[13px]" data-testid="sk-mock-kostgrunnlag">
            <thead><tr className="text-[11.5px] font-medium text-muted-foreground"><th className="pb-1.5 text-left font-medium">Produkttype</th><th className="pb-1.5 text-right font-medium">Antall</th>{!nar && <th className="pb-1.5 pl-6 text-left font-medium">Grunnlag</th>}<th className="pb-1.5 pl-4 text-left font-medium">Vurdering</th></tr></thead>
            <tbody className="divide-y divide-black/[0.06]">
              {COST_BASIS.map((c) => (
                <tr key={c.code} className="k-row">
                  <td className="py-2 font-mono text-[12.5px] text-foreground">{c.code}</td>
                  <td className="py-2 text-right tabular-nums text-foreground/80">{c.n}</td>
                  {!nar && <td className="py-2 pl-6 text-foreground/80">{c.basis} <span className="text-muted-foreground">· {c.age}</span></td>}
                  <td className={`py-2 pl-4 ${c.tone === "warn" ? "text-[hsl(var(--v-assumed))]" : "text-muted-foreground"}`}>{c.adj}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table className="mt-7 w-full border-collapse text-[13.5px]">
            <thead><tr className="text-[11.5px] font-medium text-muted-foreground"><th className="pb-2 text-left font-medium">Leveranse</th><th className="pb-2 text-right font-medium">Poster</th><th className="pb-2 text-right font-medium">DG %</th><th className="pb-2 text-right font-medium">Salg</th></tr></thead>
            <tbody className="divide-y divide-black/[0.06]">
              {GROUPS.map((g) => (
                <tr key={g.key} className="k-row">
                  <td className="py-2.5 font-medium text-foreground"><span className="inline-flex items-center gap-2">{g.name}<Check className="h-3.5 w-3.5 text-[hsl(var(--status-ok))]" strokeWidth={2.4} /></span></td>
                  <td className="py-2.5 text-right tabular-nums text-foreground/80">{g.n}</td>
                  <td className="py-2.5 text-right tabular-nums text-foreground/80">{g.dg}</td>
                  <td className="k-amount py-2.5 text-right tabular-nums text-foreground">{nok(g.sum)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-8 k-label">Utenfor fastprissummen</div>
          <Line title="1 post etter regning" sub="5.2 Reise og opphold ved idriftsettelse" right={<><span className="mr-1.5 text-[11.5px]">anslag</span><span className="font-medium text-foreground">24 000 kr</span></>} />
          <Line title="2 opsjoner" sub="6.1 Reservedelspakke · 6.2 Nødbetjening AISI 316" right={<span className="font-medium text-foreground">145 000 kr</span>} />
          <div className="mt-8 flex items-center justify-between"><span className="k-label">Tilbud</span><span className="text-[13px] font-medium text-[hsl(var(--navy))]">Åpne tilbudet</span></div>
          <Line title={ctrl ? "Versjon 1 · teknisk godkjent" : stage === "tilbud" ? "Versjon 1 · utkast" : "Ingen versjon ennå"} sub={ctrl ? `${ENGINEER.name} · i dag` : "Kalkylen er komplett – versjonen kan fryses"} right={<span className="font-medium text-foreground">{CASE.sumText} kr</span>} />
        </div>
        <aside>
          <div className="k-label">Trenger oppmerksomhet</div>
          <div className="mt-3 flex items-baseline justify-between"><span className="text-[15px] font-semibold text-foreground">7 av 7 poster ferdig</span><span className="text-[12.5px] text-muted-foreground tabular-nums">100 %</span></div>
          <div className="mt-2 h-[3px] w-full rounded-full bg-black/[0.07]"><div className="h-full w-full rounded-full bg-[hsl(var(--navy))]" /></div>
          <div className="mt-1.5 text-[12px] text-muted-foreground">6 fastpris · 1 etter regning · 2 opsjoner</div>
          <div className="mt-4">
            <Line title={ctrl ? "Teknisk godkjent" : "6 ventiler krever ingeniørvurdering"} sub={ctrl ? `${ENGINEER.name} godkjente 6 enkeltvirkende · bundet til konfigurasjonen` : "RE2/RF2 – kurvegrunnlag ikke dokumentert · går til Anders"} />
            <Line title="Kostgrunnlag: 2 produkttyper bygger på små ordrer" sub="RA2 10 (siste ordre 5 stk) og RE2/RF2 (2 stk) er seriejustert – bekreft med innkjøp før versjon 1" />
            <Line title={`Etterkalkyle ${ETTERKALKYLE.name}: ${ETTERKALKYLE.diff}`} sub={`tilbudt ${ETTERKALKYLE.offered} · faktisk ${ETTERKALKYLE.actual} · ${ETTERKALKYLE.note}`} />
            <Line title="Kvalitetssjekk · 1 funn" sub="i dag · DG 5 % på dokumentasjon er under standard (10 %)" />
            <Line title="Kost hentet fra Dynamics 14. okt." sub="standardkost, siste produksjonsordre og antall per varenummer · ingen priser merket utdatert" tone="muted" />
          </div>
        </aside>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ Tilbud */

const TOC = ["Følgebrev", "Om leveransen", "Leveranse og priser", "Opsjoner", "Forutsetninger", "Forbehold og avgrensninger", "Generelle forutsetninger", "Vilkår og betingelser", "Gyldighet og leveringstid", "Avslutning"];

const TilbudBody = ({ stage, nar, ns = "sk-sak" }) => {
  const ctrl = stage === "kontroll";
  return (
    <div className="flex h-full min-h-0 flex-col bg-[hsl(210_22%_95%)]" data-testid="sk-mock-tilbud">
      <div className={`shrink-0 border-b border-black/[0.05] bg-white ${nar ? "px-4 py-3" : "px-7 pb-3 pt-4"}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[20px] font-semibold tracking-[-0.01em] text-foreground">Tilbud {CASE.number} <span className={`inline-flex items-center gap-1 text-[14px] font-medium ${ctrl ? "text-[hsl(var(--status-ok))]" : "text-muted-foreground"}`}>{ctrl ? <><ShieldCheck className="h-3.5 w-3.5" /> Versjon 1 · Godkjent</> : stage === "tilbud" ? "Versjon 1" : "Utkast"} <ChevronDown className="h-3.5 w-3.5" /></span></div>
            <div className="mt-0.5 text-[12.5px] text-muted-foreground">{CASE.customer} · {CASE.vessel}</div>
          </div>
          {!nar && (
            <div className="flex items-center gap-2">
              <Segmented value="rediger" onChange={() => {}} items={[{ key: "rediger", label: "Rediger" }, { key: "forhandsvis", label: "Forhåndsvis" }, { key: "endringer", label: "Endringer" }]} size="sm" layoutId={`${ns}-tilbud-mode`} ariaLabel="Modus" />
              <Ghost icon={Download}>PDF</Ghost>
              <Ghost icon={Languages}>Engelsk utgave</Ghost>
              <Ghost icon={FileText}>COMOS-liste</Ghost>
              <Primary>{ctrl ? "Send til kunde" : stage === "tilbud" ? "Til teknisk godkjenning" : "Lag versjon 1"}</Primary>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground/70"><MoreHorizontal className="h-4 w-4" /></span>
            </div>
          )}
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
          <span>Sum eks. mva <span className="font-semibold tabular-nums text-foreground">{CASE.sumText}</span></span><span>·</span><span>7 poster</span><span>·</span><span>2 opsjoner utenfor sum</span><span>·</span><span>{ctrl ? `Teknisk godkjent av ${ENGINEER.name}` : `Sist endret i dag av ${USER.name}`}</span>
        </div>
        <div className="mt-2.5 rounded-[10px] border border-black/[0.06] bg-[hsl(var(--sidebar))] px-3.5 py-2 text-[13px] text-foreground/85">{ctrl ? "Versjonen er teknisk godkjent og uendret siden. Den utfylte COMOS-ventillisten følger tilbudet. Tilbudet kan sendes." : "Alle 54 ventiler har dokumentert grunnlag; 6 enkeltvirkende venter på ingeniørens godkjenning før versjon 1 kan sendes."}</div>
      </div>
      <div className={`mb-noscroll flex min-h-0 flex-1 gap-6 overflow-hidden ${nar ? "px-3 py-4" : "px-7 py-6"}`}>
        {!nar && (
          <nav className="w-[180px] shrink-0 text-[12.5px]">
            <ul className="space-y-1.5">{TOC.map((t, i) => <li key={t} className={`flex items-center justify-between ${i === 0 ? "font-medium text-foreground" : "text-foreground/65"}`}>{t}{i === 3 ? <span className="h-1.5 w-1.5 rounded-full border border-black/[0.2]" /> : null}</li>)}</ul>
            <div className="mt-6 border-t border-black/[0.08] pt-4 k-label">Versjoner</div>
            <ul className="mt-2 space-y-2">
              <li className="flex items-start gap-2"><span className="mt-1.5 h-2 w-2 rounded-full bg-[hsl(var(--navy))]" /><div><div className="font-medium text-foreground">Utkast</div><div className="text-[11.5px] text-muted-foreground">følger kalkylen</div></div></li>
              {stage !== "konfig" && stage !== "kalkyle" && <li className="flex items-start gap-2"><span className={`mt-1.5 h-2 w-2 rounded-full ${ctrl ? "bg-[hsl(var(--status-ok))]" : "border border-black/[0.3]"}`} /><div><div className="font-medium text-foreground">Versjon 1 <span className="ml-1 font-mono text-[11px] text-muted-foreground">{CASE.sumText}</span></div><div className="text-[11.5px] text-muted-foreground">{ctrl ? `godkjent · ${ENGINEER.initials}` : `i dag · ${USER.first}`}</div></div></li>}
            </ul>
          </nav>
        )}
        <div className="min-w-0 flex-1">
          <div className="rounded-[18px] bg-white shadow-[0_1px_2px_hsl(212_40%_16%/0.06),0_24px_60px_-30px_hsl(212_40%_16%/0.35)]">
            <QuoteView vm={QUOTE_VM} wide={!nar} status={ctrl ? "kontrollert" : "utkast"} texts={QUOTE_TEXT_META} customerView={false} />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ saken */

const BODY = { oversikt: OversiktBody, omfang: OmfangBody, ventiler: VentilerBody, visuelt: VisueltBody, konfig: KonfigBody, kalkyle: KalkyleBody, tilbud: TilbudBody };

export const SkSak = ({ beat, tab, onTab = () => {}, mode = "full", ns = "sk-sak" }) => {
  const Body = BODY[tab] || OversiktBody;
  const nar = mode === "narrow";
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden" data-testid="sk-sak" data-tab={tab} data-stage={beat.stage}>
      <Head tab={tab === "visuelt" ? "ventiler" : tab} onTab={onTab} stage={beat.stage} nar={nar} layoutId={`${ns}-tabs`} />
      <div key={tab + beat.stage} className="mb-fade mb-noscroll min-h-0 flex-1 overflow-hidden"><Body stage={beat.stage} nar={nar} mode={mode} ns={ns} /></div>
    </div>
  );
};

export const SkSakApp = ({ beat, tab, onTab, mode = "full", ns = "sk-sak", panel = true }) => (
  <Shell mode={mode} active="arbeid" panel={panel ? <WorkPanel /> : null} title="Arbeid" toast={beat.toast} testId="sk-sak-app" view="sak">
    <SkSak beat={beat} tab={tab} onTab={onTab} mode={mode} ns={ns} />
  </Shell>
);

/* kjører manuset; et klikk på en fane tar over en stund */
export const useScript = (active) => {
  const [i, setI] = useState(0);
  const [manual, setManual] = useState(null);
  useEffect(() => {
    if (!active || manual) return undefined;
    const t = setTimeout(() => setI((x) => (x + 1) % SCRIPT.length), SCRIPT[i].ms);
    return () => clearTimeout(t);
  }, [i, active, manual]);
  useEffect(() => {
    if (!manual) return undefined;
    const t = setTimeout(() => setManual(null), 9000);
    return () => clearTimeout(t);
  }, [manual]);
  const beat = SCRIPT[i];
  return { beat: manual ? { ...beat, toast: null } : beat, tab: manual || beat.tab, onTab: setManual, i };
};
